import { createSubscriber } from 'svelte/reactivity';

import { readJson, readText, writeJson, writeText } from '#lib/platform/local-json.js';

import { sanitizePayload, scrubMatrixIds } from './scrubbers.js';

export type DebugLogLevel = 'debug' | 'info' | 'warn' | 'error';
export type DebugLogCategory =
  | 'sync'
  | 'network'
  | 'notification'
  | 'message'
  | 'media'
  | 'call'
  | 'ui'
  | 'timeline'
  | 'error'
  | 'general';

export type DebugLogEntry = {
  id: number;
  timestamp: number;
  level: DebugLogLevel;
  category: DebugLogCategory;
  namespace: string;
  message: string;
  data?: unknown;
};

const MAX_ENTRIES = 1000;
const TRIM_SLACK = 200;
const ENABLED_KEY = 'sable_internal_debug';
const DISABLED_CATEGORIES_KEY = 'sable-debug-disabled-categories';
const consoleMethods = ['error', 'warn', 'info', 'debug'] as const;

type ConsoleMethod = (typeof consoleMethods)[number];
type ConsoleInterceptor = {
  active: boolean;
  original: (...args: unknown[]) => void;
  wrapped: (...args: unknown[]) => void;
};

function readEnabled(): boolean {
  return readText(ENABLED_KEY) === '1';
}

function readDisabledCategories(): DebugLogCategory[] {
  return readJson(
    DISABLED_CATEGORIES_KEY,
    (value) =>
      Array.isArray(value)
        ? (value.filter((item) => typeof item === 'string') as DebugLogCategory[])
        : [],
    []
  );
}

const buffer: DebugLogEntry[] = [];
/* eslint-disable svelte/prefer-svelte-reactivity */
const disabledCategories = new Set<DebugLogCategory>(readDisabledCategories());
const consoleInterceptors = new Map<ConsoleMethod, ConsoleInterceptor>();
const captureListeners = new Set<(enabled: boolean) => void>();
/* eslint-enable svelte/prefer-svelte-reactivity */

let enabled = readEnabled();
let nextId = 1;
let lastEntry: DebugLogEntry | null = null;
let interceptingConsole = false;
let notify: (() => void) | undefined;
let notifyScheduled = false;

const subscribe = createSubscriber((update) => {
  notify = update;
  return () => {
    notify = undefined;
    notifyScheduled = false;
  };
});

function scheduleNotify(): void {
  if (notify === undefined || notifyScheduled) return;
  notifyScheduled = true;
  const flush = (): void => {
    notifyScheduled = false;
    notify?.();
  };
  if (typeof requestAnimationFrame === 'function') requestAnimationFrame(flush);
  else queueMicrotask(flush);
}

function append(entry: Omit<DebugLogEntry, 'id'>): void {
  if (
    lastEntry?.level === entry.level &&
    lastEntry.namespace === entry.namespace &&
    lastEntry.message === entry.message
  ) {
    return;
  }
  const stored: DebugLogEntry = { id: nextId, ...entry };
  nextId += 1;
  buffer.push(stored);
  lastEntry = stored;
  if (buffer.length > MAX_ENTRIES + TRIM_SLACK) buffer.splice(0, buffer.length - MAX_ENTRIES);
  scheduleNotify();
}

export const debugLog = {
  get enabled(): boolean {
    subscribe();
    return enabled;
  },
  get entries(): readonly DebugLogEntry[] {
    subscribe();
    return buffer;
  },
  get disabledCategories(): ReadonlySet<DebugLogCategory> {
    subscribe();
    return disabledCategories;
  },
};

function formatConsoleArgs(args: unknown[]): string {
  return args
    .map((arg) => {
      if (arg instanceof Error) return arg.stack ?? arg.message;
      if (typeof arg === 'string') return arg;
      try {
        return JSON.stringify(sanitizePayload(arg));
      } catch {
        return String(arg);
      }
    })
    .join(' ');
}

function interceptConsole(): void {
  if (consoleInterceptors.size > 0) return;
  for (const method of consoleMethods) {
    const original = console[method].bind(console);
    const interceptor: ConsoleInterceptor = { active: true, original, wrapped };
    function wrapped(...args: unknown[]): void {
      if (!interceptor.active || interceptingConsole) {
        original(...args);
        return;
      }

      interceptingConsole = true;
      try {
        append({
          timestamp: Date.now(),
          level: method === 'error' ? 'error' : method === 'warn' ? 'warn' : method,
          category: method === 'error' ? 'error' : 'general',
          namespace: 'console',
          message: scrubMatrixIds(formatConsoleArgs(args)),
        });
        original(...args);
      } finally {
        interceptingConsole = false;
      }
    }
    consoleInterceptors.set(method, interceptor);
    console[method] = wrapped;
  }
}

function restoreConsole(): void {
  for (const method of consoleMethods) {
    const interceptor = consoleInterceptors.get(method);
    if (!interceptor) continue;
    interceptor.active = false;
    if (console[method] === interceptor.wrapped) console[method] = interceptor.original;
  }
  consoleInterceptors.clear();
}

export function setDebugLogging(next: boolean): void {
  enabled = next;
  if (next) interceptConsole();
  else restoreConsole();
  writeText(ENABLED_KEY, next ? '1' : '0');
  notify?.();
  for (const listener of captureListeners) listener(next);
}

export function onDebugLogCapture(listener: (enabled: boolean) => void): () => void {
  captureListeners.add(listener);
  listener(enabled);
  return () => {
    captureListeners.delete(listener);
  };
}

if (enabled) interceptConsole();

export function setDebugCategoryEnabled(category: DebugLogCategory, next: boolean): void {
  if (next) disabledCategories.delete(category);
  else disabledCategories.add(category);
  writeJson(DISABLED_CATEGORIES_KEY, [...disabledCategories]);
  notify?.();
}

export function recordDebugLog(
  level: DebugLogLevel,
  category: DebugLogCategory,
  namespace: string,
  message: string,
  data?: unknown
): void {
  if (!enabled || disabledCategories.has(category)) return;
  append({
    timestamp: Date.now(),
    level,
    category,
    namespace,
    message: scrubMatrixIds(message),
    ...(data === undefined ? {} : { data: sanitizePayload(data) }),
  });
}

export function clearDebugLogs(): void {
  buffer.length = 0;
  lastEntry = null;
  notify?.();
}

export function exportDebugLogs(entries: readonly DebugLogEntry[] = buffer): string {
  /* eslint-disable-next-line svelte/prefer-svelte-reactivity */
  const iso = (value?: number): string => new Date(value ?? Date.now()).toISOString();
  return JSON.stringify(
    {
      exportedAt: iso(),
      logsCount: entries.length,
      logs: entries.map((entry) => ({
        ...entry,
        timestamp: iso(entry.timestamp),
        namespace: scrubMatrixIds(entry.namespace),
        message: scrubMatrixIds(entry.message),
        ...(entry.data === undefined ? {} : { data: sanitizePayload(entry.data) }),
      })),
    },
    null,
    2
  );
}
